import { useState } from "react";
import { X } from "lucide-react";

const empty = {
  title: "",
  address: "",
  city: "Houston, TX",
  neighborhood: "The Heights",
  price: 0,
  beds: 3,
  baths: 2,
  sqft: 1800,
  year_built: 2020,
  property_type: "House",
  status: "For Sale",
  featured: false,
  description: "",
  images: [],
  features: [],
};

const NEIGHBORHOODS = [
  "River Oaks", "The Heights", "Memorial", "Montrose", "Midtown",
  "West University", "Bellaire", "Sugar Land", "The Woodlands", "Katy",
  "Richmond", "Cypress", "Spring", "Conroe", "Tomball", "Fulshear",
];
const TYPES = ["House", "Condo", "Townhouse"];
const STATUSES = ["For Sale", "Pending", "Sold"];

export default function PropertyForm({ initial, onClose, onSubmit }) {
  const [f, setF] = useState(() => {
    if (!initial) return empty;
    return { ...empty, ...initial };
  });
  const [imagesText, setImagesText] = useState((initial?.images || []).join("\n"));
  const [featuresText, setFeaturesText] = useState((initial?.features || []).join("\n"));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...f,
      price: Number(f.price) || 0,
      beds: Number(f.beds) || 0,
      baths: Number(f.baths) || 0,
      sqft: Number(f.sqft) || 0,
      year_built: Number(f.year_built) || new Date().getFullYear(),
      images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    await onSubmit(payload, initial?.id);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto">
      <form
        onSubmit={submit}
        className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl my-auto"
        data-testid="property-form"
      >
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <h2 className="font-display text-2xl font-semibold">
            {initial ? "Edit listing" : "New listing"}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-[#E63946]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-7 space-y-5 max-h-[70vh] overflow-y-auto">
          <Field label="Title">
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} required data-testid="form-title" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Address"><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={input} required /></Field>
            <Field label="City / Zip"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={input} /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Neighborhood">
              <select value={f.neighborhood} onChange={(e) => setF({ ...f, neighborhood: e.target.value })} className={input}>
                {NEIGHBORHOODS.map((n) => <option key={n}>{n}</option>)}
              </select>
            </Field>
            <Field label="Property Type">
              <select value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })} className={input}>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={input}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Field label="Price ($)"><input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={input} /></Field>
            <Field label="Beds"><input type="number" value={f.beds} onChange={(e) => setF({ ...f, beds: e.target.value })} className={input} /></Field>
            <Field label="Baths"><input type="number" step="0.5" value={f.baths} onChange={(e) => setF({ ...f, baths: e.target.value })} className={input} /></Field>
            <Field label="SqFt"><input type="number" value={f.sqft} onChange={(e) => setF({ ...f, sqft: e.target.value })} className={input} /></Field>
            <Field label="Year Built"><input type="number" value={f.year_built} onChange={(e) => setF({ ...f, year_built: e.target.value })} className={input} /></Field>
          </div>
          <Field label="Description">
            <textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={input + " resize-none"} />
          </Field>
          <Field label="Image URLs (one per line)">
            <textarea rows={3} value={imagesText} onChange={(e) => setImagesText(e.target.value)} className={input + " resize-none font-mono text-xs"} placeholder="https://..." />
          </Field>
          <Field label="Features (one per line)">
            <textarea rows={3} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} className={input + " resize-none"} placeholder="Pool&#10;Smart Home&#10;Chef's Kitchen" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!f.featured}
              onChange={(e) => setF({ ...f, featured: e.target.checked })}
              className="w-4 h-4 accent-[#E63946]"
              data-testid="form-featured"
            />
            Feature this listing on the homepage
          </label>
        </div>
        <div className="flex items-center justify-end gap-3 px-7 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-gray-600 hover:text-[#1A1A1A]">Cancel</button>
          <button type="submit" disabled={busy} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-2.5 rounded-md text-sm font-semibold disabled:opacity-50" data-testid="form-save">
            {busy ? "Saving…" : initial ? "Save Changes" : "Create Listing"}
          </button>
        </div>
      </form>
    </div>
  );
}

const input = "w-full bg-stone-50 border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#E63946]";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
